import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Shuffle, Lightbulb, RotateCcw, Flame, CalendarCheck } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAudio, useTrack } from "@/context/GameFeel";
import { VoltSay } from "@/components/Volt";

// 36 kinds: 0-8 dots, 9-17 bamboo, 18-26 characters, 27-30 winds, 31-33 dragons, 34 flowers, 35 seasons
const GLYPH = (k) => k < 18 ? "🎋" : k < 27 ? ["一", "二", "三", "四", "五", "六", "七", "八", "九"][k - 18]
  : k < 31 ? ["東", "南", "西", "北"][k - 27] : k < 34 ? ["中", "發", "□"][k - 31] : k === 34 ? "🌸" : "🍂";
const LABEL = (k) => k < 9 ? `${k + 1}` : k < 18 ? `${k - 8}` : k < 27 ? "" : "";
const TONE = (k) => k < 9 ? "text-sky-200" : k < 18 ? "text-emerald-200" : k < 27 ? "text-rose-200" : k < 31 ? "text-amber-200" : k < 34 ? "text-fuchsia-200" : "text-pink-300";
// dot (circle) suits drawn as a 3x3 pip grid so pips always stay inside the tile face
const PIPS = [[4], [0, 8], [0, 4, 8], [0, 2, 6, 8], [0, 2, 4, 6, 8], [0, 2, 3, 5, 6, 8], [0, 2, 3, 4, 5, 6, 8], [0, 1, 2, 3, 5, 6, 7, 8], [0, 1, 2, 3, 4, 5, 6, 7, 8]];
const Face = ({ k }) => k < 9
  ? <span className="grid grid-cols-3 gap-[3px] w-[30px] h-[30px] mx-auto mt-[14px]" aria-hidden>
      {Array.from({ length: 9 }, (_, c) => <span key={c} className={`w-2 h-2 rounded-full ${PIPS[k].includes(c) ? "bg-sky-700 ring-1 ring-sky-900/60" : "bg-transparent"}`} />)}
    </span>
  : <span className={`block text-[22px] leading-[56px] font-black ${TONE(k).replace("200", "700").replace("300", "600")}`}>{GLYPH(k)}</span>;
const covers = (a, b) => a[2] === b[2] + 1 && Math.abs(a[0] - b[0]) < 2 && Math.abs(a[1] - b[1]) < 2;
const side = (a, b, dx) => a[2] === b[2] && b[0] === a[0] + dx && Math.abs(a[1] - b[1]) < 2;

function freeSet(layout, present) {
  const ids = [...present]; const out = new Set();
  for (const i of ids) {
    const p = layout[i];
    if (ids.some((j) => covers(layout[j], p))) continue;
    const l = ids.some((j) => side(p, layout[j], -2)), r = ids.some((j) => side(p, layout[j], 2));
    if (!(l && r)) out.add(i);
  }
  return out;
}
const findPair = (layout, tiles, present) => {
  const f = [...freeSet(layout, present)];
  for (let a = 0; a < f.length; a++) for (let b = a + 1; b < f.length; b++) if (tiles[f[a]] === tiles[f[b]]) return [f[a], f[b]];
  return null;
};
const W = 740, H = 510; // board px at scale 1 (half-unit = 24px wide, 30px tall; tile 46x58)

export default function Mahjong() {
  const { search } = useLocation();
  const daily = new URLSearchParams(search).get("daily") === "1";
  const { user, setUser } = useAuth();
  const { sfx, react } = useAudio();
  useTrack("play");
  const [board, setBoard] = useState(null);
  const [tiles, setTiles] = useState([]);
  const [present, setPresent] = useState(new Set());
  const [sel, setSel] = useState(null);
  const [hint, setHint] = useState(null);
  const [history, setHistory] = useState([]);
  const [done, setDone] = useState(null);
  const [scale, setScale] = useState(1);
  const wrap = useRef(null); const t0 = useRef(Date.now());

  const load = async () => {
    try {
      const { data } = await api.get(daily ? "/mahjong/daily" : "/mahjong/board");
      setBoard(data); setTiles(data.tiles); setPresent(new Set(data.tiles.map((_, i) => i))); setSel(null); setHint(null); setHistory([]); setDone(null); t0.current = Date.now();
      if (!daily) api.post("/mahjong/played").catch(() => {});
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, [daily]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fit = () => { if (wrap.current) setScale(Math.min(1, (wrap.current.clientWidth - 16) / W)); };
    fit(); window.addEventListener("resize", fit); return () => window.removeEventListener("resize", fit);
  }, [board]);

  const free = useMemo(() => board ? freeSet(board.layout, present) : new Set(), [board, present]);
  const stuck = board && present.size > 0 && !findPair(board.layout, tiles, present);

  const tap = (i) => {
    if (!free.has(i) || done) return;
    sfx("tap"); setHint(null);
    if (sel === null) return setSel(i);
    if (sel === i) return setSel(null);
    if (tiles[sel] !== tiles[i]) { sfx("wrong"); return setSel(i); }
    const next = new Set(present); next.delete(sel); next.delete(i);
    setHistory((h) => [...h, [sel, i]]); setPresent(next); setSel(null); sfx("correct");
    if (next.size === 0) finish();
  };
  const undo = () => { const last = history[history.length - 1]; if (!last || done) return; const n = new Set(present); last.forEach((x) => n.add(x)); setPresent(n); setHistory((h) => h.slice(0, -1)); setSel(null); };
  const shuffle = () => {
    if (done) return;
    const ids = [...present]; const kinds = ids.map((i) => tiles[i]);
    for (let k = kinds.length - 1; k > 0; k--) { const j = Math.floor(Math.random() * (k + 1)); [kinds[k], kinds[j]] = [kinds[j], kinds[k]]; }
    const t = [...tiles]; ids.forEach((i, n) => { t[i] = kinds[n]; }); setTiles(t); setSel(null); setHint(null); sfx("whoosh");
  };
  const showHint = () => { const p = findPair(board.layout, tiles, present); if (p) { setHint(p); sfx("tick"); } else toast("No free pair — try Shuffle"); };

  const finish = async () => {
    const time_ms = Date.now() - t0.current;
    if (!daily) { react({ outcome: "win", game: "mahjong", bubble: false }); return setDone({ mode: "free", time_ms }); }
    try {
      const { data } = await api.post("/mahjong/daily/complete", { board_id: board.board_id, tiles_left: 0, time_ms });
      if (data.coins != null) setUser((u) => ({ ...u, coins: data.coins }));
      react({ outcome: "win", game: "mahjong", bubble: false }); if (data.bonus) setTimeout(() => sfx("streak"), 400);
      setDone({ mode: "daily", time_ms, ...data });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleReminder = async (opt_in) => {
    try {
      const { data } = await api.post("/mahjong/reminder", { opt_in });
      setBoard((b) => ({ ...b, reminder: data.reminder }));
      toast.success(opt_in ? "Evening reminder saved" : "Reminder turned off");
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!board) return <div className="p-10 text-center text-gray-400">Setting up the tiles…</div>;
  const mmss = (ms) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}`;

  return (
    <div data-testid="mahjong-page" className="p-3 sm:p-6 max-w-4xl mx-auto">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Link to="/casino" data-testid="mahjong-back" className="text-sm text-gray-400 hover:text-white">← Lobby</Link>
        <h1 className="font-display text-2xl sm:text-3xl font-black ml-1">{daily ? "Daily Puzzle" : "Mahjong Solitaire"}</h1>
        {daily && <span data-testid="daily-streak-badge" className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-500/15 text-amber-300"><Flame className="w-3.5 h-3.5" /> {board.streak}-day streak</span>}
        <span data-testid="mahjong-tiles-left" className="ml-auto text-sm text-gray-400">{present.size} tiles left</span>
      </div>
      <p className="text-xs text-gray-500 mb-3">Match two free tiles of the same kind (flowers match flowers, seasons match seasons). Free play · Fun Coins only.</p>
      {daily && (
        <label data-testid="daily-reminder-label" className="flex items-start gap-2 text-sm text-gray-300 cursor-pointer glass rounded-xl border border-white/10 px-3 py-2.5 mb-3">
          <input data-testid="daily-reminder-checkbox" type="checkbox" className="mt-1 accent-amber-400" checked={!!board.reminder?.opt_in} onChange={(e) => toggleReminder(e.target.checked)} />
          <span>Email me an evening reminder around 8 PM ET for tomorrow's puzzle (optional; you can turn it off anytime).</span>
        </label>
      )}
      <div className="flex gap-2 mb-3">
        <button data-testid="mahjong-hint" onClick={showHint} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 text-sm hover:bg-white/10"><Lightbulb className="w-4 h-4" /> Hint</button>
        <button data-testid="mahjong-shuffle" onClick={shuffle} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 text-sm hover:bg-white/10"><Shuffle className="w-4 h-4" /> Shuffle</button>
        <button data-testid="mahjong-undo" onClick={undo} disabled={!history.length} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 text-sm hover:bg-white/10 disabled:opacity-40"><RotateCcw className="w-4 h-4" /> Undo</button>
        {!daily && <button data-testid="mahjong-new" onClick={load} className="ml-auto px-3 py-2 rounded-xl bg-white/5 text-sm hover:bg-white/10">New board</button>}
      </div>
      {stuck && !done && <p data-testid="mahjong-stuck" className="text-sm text-amber-300 mb-2">No moves left — use Shuffle to keep going.</p>}

      <div ref={wrap} className="w-full overflow-hidden rounded-2xl glass border border-white/10" style={{ height: H * scale + 16 }}>
        <div data-testid="mahjong-board" className="relative origin-top-left" style={{ width: W, height: H, transform: `translate(8px, 8px) scale(${scale})` }}>
          {board.layout.map((p, i) => present.has(i) && (
            <button key={i} type="button" data-testid={`tile-${i}`} data-kind={tiles[i]} data-free={free.has(i)} onClick={() => tap(i)}
              className={`absolute w-[46px] h-[58px] rounded-md border text-center select-none overflow-hidden transition-transform duration-150 ${free.has(i) ? "cursor-pointer hover:-translate-y-0.5" : "cursor-default"}
                ${sel === i ? "ring-2 ring-amber-400 border-amber-300 bg-amber-100" : hint && hint.includes(i) ? "ring-2 ring-cyan-400 border-cyan-300 bg-cyan-50" : free.has(i) ? "bg-[#f4ecd8] border-[#d6c7a1]" : "bg-[#cfc5ad] border-[#b5a98a] opacity-90"}`}
              style={{ left: 60 + p[0] * 24 - p[2] * 6, top: 20 + p[1] * 30 - p[2] * 8, zIndex: p[2] * 200 + p[1] * 4 + (24 - p[0]), boxShadow: "2px 3px 0 #8b7d5a" }}>
              <Face k={tiles[i]} />
              {LABEL(tiles[i]) && <span className="absolute top-0.5 left-1 text-[9px] font-bold text-gray-600 leading-none">{LABEL(tiles[i])}</span>}
            </button>
          ))}
        </div>
      </div>

      {done && (
        <div data-testid="mahjong-done" className="mt-4 glass rounded-2xl border border-emerald-500/30 p-5 text-center">
          <p className="font-display text-2xl font-black text-emerald-300">Board cleared in {mmss(done.time_ms)}!</p>
          {done.mode === "daily" && (
            <div className="mt-3 space-y-3">
              <p data-testid="daily-streak-result" className="text-lg font-semibold flex items-center justify-center gap-2"><Flame className="w-5 h-5 text-amber-400" /> {done.streak}-day streak{done.already_completed ? " · already counted today" : ""}</p>
              {done.bonus > 0 && <p data-testid="daily-bonus" className="text-amber-300 font-semibold">Streak bonus: +{done.bonus.toLocaleString()} Fun Coins</p>}
              {done.next_milestone && <p className="text-sm text-gray-400">Next bonus at {done.next_milestone} days (+{done.milestones[done.next_milestone].toLocaleString()} Fun Coins)</p>}
              <VoltSay testid="volt-daily-puzzle" text="Come back tomorrow — a fresh puzzle unlocks every day, same board for everyone." mood="cheer" size={48} />
            </div>
          )}
          <div className="flex flex-wrap gap-2 justify-center mt-4">
            <Link to="/casino/mahjong" data-testid="mahjong-rematch" onClick={() => !daily && load()} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 font-semibold">{daily ? "Play another board" : "Rematch"}</Link>
            {!daily && <Link to="/casino/mahjong?daily=1" data-testid="mahjong-go-daily" className="px-5 py-2.5 rounded-xl bg-white/10 font-semibold flex items-center gap-1.5"><CalendarCheck className="w-4 h-4" /> Today's Daily Puzzle</Link>}
            <Link to="/casino" className="px-5 py-2.5 rounded-xl bg-white/5 font-semibold">Lobby</Link>
          </div>
        </div>
      )}
    </div>
  );
}
